import { Word } from '@goldcast/player-config/src/types/transcript-types';
import { Clip, DeletedRange, EditedWord } from '@goldcast/player-config/src/types/clip-types';
import {
  ClipDeletes,
  CompositeSegment
} from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Segment/CompositeSegment';
import { MAIN_TRACK_ID } from '@goldcast/player-config/src/constants/player';
import { mergeDeletes } from '@goldcast/player-config/src/utils/assetMetadataUtils';
import {
  SearchParagraphWord,
  TranscriptExtension,
  TranscriptSelection,
  TranscriptSelectionRange,
  TranscriptSelectionRanges
} from '../types';
import { saveClipChanges, updateClipCaptionsLoadingState, updateClipWithId } from '@/stores/clip';
import { markCaptionsObsolete } from '@/stores/captions';
import { addDecimalIfNeeded } from '@/libs/utils';
import { getEditsForSegment, getMergesForSegment } from '@/Pages/Clip/ClipPlayer/ClipNewTimeline/playerConfigUtil';

/**
 *
 * @param selectionRanges ranges of words selected in transcript
 * @returns new deletes that are inside active words selected ranges
 */
export function getDeletesFromSelectedRanges(selectionRanges: TranscriptSelectionRanges | null): ClipDeletes {
  return selectionRanges
    ? selectionRanges?.active.reduce((acc: ClipDeletes, el: TranscriptSelectionRange) => {
        return {
          ...acc,
          [el.start]: {
            prev_end_time: el.start,
            next_start_time: el.end,
            bounds: [el.start, el.end]
          }
        };
      }, {})
    : {};
}

/**
 *
 * @param words that we need boundaries for
 * @returns first and last words on the words array
 */
export function getWordsBoundaries(words: Word[]): { firstWord: Word; lastWord: Word } {
  return {
    firstWord: words[0],
    lastWord: words[words.length - 1]
  };
}

/**
 *
 * @param firstSelectedWord from transcript selection
 * @param lastSelectedWord from transcript selection
 * @param clipDeletes from editing clip
 * @returns restoring & keepeing deletes based on selection. In case we select words from
 * multiple different deletes, all deletes would be part of restoring object
 */
export function getSelectionPartitionedDeletes(
  firstSelectedWord: Word,
  lastSelectedWord: Word,
  clipDeletes: ClipDeletes
): {
  restoring: ClipDeletes;
  keeping: ClipDeletes;
} {
  return Object.values(clipDeletes).reduce(
    (acc: { restoring: ClipDeletes; keeping: ClipDeletes }, deleteItem: DeletedRange) => {
      if (lastSelectedWord.end_time >= deleteItem.bounds[0] && firstSelectedWord.start_time <= deleteItem.bounds[1]) {
        acc.restoring[deleteItem.bounds[0]] = deleteItem;
      } else {
        acc.keeping[deleteItem.bounds[0]] = deleteItem;
      }
      return acc;
    },
    { restoring: {}, keeping: {} }
  );
}

export function restoreDelete(deletes: ClipDeletes, restoreRange: DeletedRange) {
  // Find and remove deletes that overlap with the selected time range
  const newDeletes = structuredClone(deletes);
  const restoreDeleteStartTime = restoreRange.bounds[0];
  const restoreDeleteEndTime = restoreRange.bounds[1];

  const keysToRemove: string[] = [];
  const keysToUpdate: { [key: string]: any } = {};

  Object.entries(newDeletes).forEach(([key, deleteItem]) => {
    const deleteStart = deleteItem.bounds[0];
    const deleteEnd = deleteItem.bounds[1];

    // Check if the selected time range overlaps with this delete range
    const hasOverlap =
      (restoreDeleteStartTime >= deleteStart && restoreDeleteStartTime < deleteEnd) ||
      (restoreDeleteEndTime > deleteStart && restoreDeleteEndTime <= deleteEnd) ||
      (restoreDeleteStartTime <= deleteStart && restoreDeleteEndTime >= deleteEnd);

    if (hasOverlap) {
      // Check if the selection completely encloses the delete range
      const isCompletelyEnclosed = restoreDeleteStartTime <= deleteStart && restoreDeleteEndTime >= deleteEnd;

      if (isCompletelyEnclosed) {
        // Completely remove the delete
        keysToRemove.push(key);
      } else {
        // Check if the selection is completely within the delete range
        const isSelectionWithinDelete = restoreDeleteStartTime >= deleteStart && restoreDeleteEndTime <= deleteEnd;

        if (isSelectionWithinDelete) {
          // Split the delete into two parts
          const firstDeleteStart = deleteStart;
          const firstDeleteEnd = restoreDeleteStartTime;
          const secondDeleteStart = restoreDeleteEndTime;
          const secondDeleteEnd = deleteEnd;

          // Remove the original delete
          keysToRemove.push(key);

          // Add the first part if it's valid
          if (firstDeleteStart < firstDeleteEnd) {
            const firstDeleteKey = firstDeleteStart.toString();
            keysToUpdate[firstDeleteKey] = {
              bounds: [firstDeleteStart, firstDeleteEnd],
              prev_end_time: firstDeleteStart,
              next_start_time: firstDeleteEnd
            };
          }

          // Add the second part if it's valid
          if (secondDeleteStart < secondDeleteEnd) {
            const secondDeleteKey = secondDeleteStart.toString();
            keysToUpdate[secondDeleteKey] = {
              bounds: [secondDeleteStart, secondDeleteEnd],
              prev_end_time: secondDeleteStart,
              next_start_time: secondDeleteEnd
            };
          }
        } else {
          // Partially overlap - update the delete bounds
          let newDeleteStart = deleteStart;
          let newDeleteEnd = deleteEnd;

          // If selection starts within the delete range, adjust the end
          if (restoreDeleteStartTime > deleteStart && restoreDeleteStartTime < deleteEnd) {
            newDeleteEnd = restoreDeleteStartTime;
          }

          // If selection ends within the delete range, adjust the start
          if (restoreDeleteEndTime > deleteStart && restoreDeleteEndTime < deleteEnd) {
            newDeleteStart = restoreDeleteEndTime;
          }

          // Only update if the new bounds are valid (start < end)
          if (newDeleteStart < newDeleteEnd) {
            keysToUpdate[key] = {
              ...deleteItem,
              bounds: [newDeleteStart, newDeleteEnd],
              prev_end_time: newDeleteStart,
              next_start_time: newDeleteEnd
            };
          } else {
            // If the bounds are invalid, remove the delete entirely
            keysToRemove.push(key);
          }
        }
      }
    }
  });

  // Remove the completely enclosed deletes
  keysToRemove.forEach(key => {
    delete newDeletes[key];
  });

  // Update the partially overlapped deletes
  Object.entries(keysToUpdate).forEach(([key, updatedDelete]) => {
    newDeletes[key] = updatedDelete;
  });

  return mergeDeletes(newDeletes);
}

/**
 * Updates the clip's word edits with a new word value for a specific word's start time.
 * In case of hide - value is sent as an empty string
 *
 * @param {Word} word - The word to be edited
 * @param {string} value - The new word content. In case of hide - value is sent as an empty string
 * @param {Clip} values - The clip to update
 * @returns {Clip} - A new clip object with the updated word edits in `asset_metadata.edits`.
 *
 * @example
 * const word = { start_time: "0.5", end_time: "1.0" };
 * const clip = { asset_metadata: { edits: {} } };
 * const updatedClip = editClipWord(word, "newValue", clip);
 * // Output: { start_time: "0.5", end_time: "1.0", word: "newValue" }
 */
export function editClipWord(
  word: Word | SearchParagraphWord,
  value: string,
  clipData: Clip,
  addToMerges: boolean = false
): Clip {
  const editedWord: EditedWord = {
    start_time: word.start_time,
    end_time: word.end_time,
    word: value
  };
  const currentWordEdit = getEditsForSegment(clipData)?.[addDecimalIfNeeded(word.start_time)];
  const isHiddenWord = currentWordEdit && !currentWordEdit.word;
  return {
    ...clipData,
    asset_metadata: {
      ...clipData.asset_metadata,
      edits: {
        ...clipData.asset_metadata?.edits,
        // we identify hidden by `word: ''`, and if we override edits, it would make it non-deleted
        ...(isHiddenWord
          ? {}
          : {
              [addDecimalIfNeeded(word.start_time)]: {
                start_time: word.start_time,
                end_time: word.end_time,
                word: value
              }
            })
      },
      // Add to merges to cover the case edit -> hide. Without this it would revert to the original content
      merges: addToMerges
        ? (getMergesForSegment(clipData) ?? [])
            .filter(({ start_time }) => start_time !== editedWord.start_time)
            .concat(editedWord)
        : (clipData.asset_metadata.merges ?? [])
    }
  };
}

/**
 * Merges a word into a clip's metadata, updating the "merges" array and "edits" object.
 *
 * This function adds an `EditedWord` to the "merges" array in the clip's metadata,
 * when correcting transcript with multiple selected words
 *
 * @param {string} word - New corrected value
 * @param {Clip} clipData - The existing clip data to be updated.
 * @param {number} startTime - The start time correcting words
 * @param {number} endTime - The end time of correcting words
 * @returns {Clip} - A new `Clip` object with updated metadata.
 *
 * @example
 * const updatedClip = mergeClipWords("new word content", clipData, 1, 5);
 * // Output:
 * // {
 * //   asset_metadata: {
 * //     merges: [
 * //       { start_time: 1, end_time: 5, word: "new word content" }
 * //     ],
 * //     edits: {
 * //       1: { start_time: 1, end_time: 5, word: "new word content" },
 * //     }
 * //   }
 * // }
 */
export function mergeClipWords(word: string, clipData: Clip, startTime: number, endTime: number): Clip {
  const editedWord: EditedWord = {
    start_time: startTime,
    end_time: endTime,
    word
  };
  return {
    ...clipData,
    asset_metadata: {
      ...clipData.asset_metadata,
      merges: (clipData.asset_metadata.merges ?? [])
        .filter(({ start_time }) => start_time !== editedWord.start_time)
        .filter(({ start_time }) => !(start_time >= editedWord.start_time && start_time <= editedWord.end_time))
        .concat(editedWord),
      edits: { ...clipData.asset_metadata.edits, [editedWord.start_time]: editedWord }
    }
  };
}

export function saveCorrectWordChanges(updatedClipValue: Clip, clipData: Clip) {
  markCaptionsObsolete({ clipId: clipData.id });
  updateClipCaptionsLoadingState({ clipId: clipData.id, loading: true });
  updateClipWithId(clipData.id, updatedClipValue, true);
  saveClipChanges({
    clipId: clipData.id,
    shouldGenerateThumbnail: !!updatedClipValue,
    shouldAddToHistory: true,
    isEditedState: clipData.asset_metadata.is_edited
  });
}

export const getSelectionBoundaryTrimUpdate = (
  segment: CompositeSegment,
  transcriptSelection?: TranscriptSelection | null,
  transcriptExtension?: TranscriptExtension
): {
  segmentId: string;
  startTime?: number;
  endTime?: number;
  trackId: string;
  originalStartTime?: number;
  originalEndTime?: number;
} | null => {
  const segmentFirstWord = segment.segment_metadata?.captions?.[0];
  const segmentLastWord = segment.segment_metadata?.captions?.[segment.segment_metadata.captions.length - 1];
  const segmentFirstSelectionWord = transcriptSelection?.words[segment.id]?.[0];
  const segmentLastSelectionWord =
    transcriptSelection?.words[segment.id]?.[transcriptSelection.words[segment.id].length - 1];

  if (!segmentFirstWord || !segmentLastWord || !segmentFirstSelectionWord || !segmentLastSelectionWord) {
    return null;
  }

  const startBoundaryTrimUpdate = getStartBoundaries({
    transcriptExtension,
    segment,
    segmentFirstWord,
    segmentFirstSelectionWord,
    transcriptSelection
  });
  if (startBoundaryTrimUpdate) {
    return startBoundaryTrimUpdate;
  }

  const endBoundaryTrimUpdate = getEndBoundaries({
    transcriptExtension,
    segment,
    segmentLastWord,
    segmentLastSelectionWord,
    transcriptSelection
  });
  if (endBoundaryTrimUpdate) {
    return endBoundaryTrimUpdate;
  }

  return null;
};

function getStartBoundaries({
  transcriptExtension,
  segment,
  segmentFirstWord,
  segmentFirstSelectionWord,
  transcriptSelection
}: {
  transcriptExtension?: TranscriptExtension | null;
  segment: CompositeSegment;
  segmentFirstWord: Word;
  segmentFirstSelectionWord: Word;
  transcriptSelection?: TranscriptSelection | null;
}) {
  // check if there is a start boundary outside of selection
  const hasStartBoundary =
    transcriptExtension &&
    'startTime' in transcriptExtension &&
    'startSegmentId' in transcriptExtension &&
    transcriptExtension.startSegmentId === segment.id &&
    segmentFirstWord?.start_time > segmentFirstSelectionWord?.start_time;

  if (hasStartBoundary) {
    const boundariesWords = transcriptSelection?.words[segment.id]?.filter(word => {
      // set new start time for this segment
      return word.start_time >= transcriptExtension.startTime;
    });

    const newStartTime = boundariesWords?.[0]?.start_time;
    if (newStartTime !== undefined) {
      return {
        segmentId: segment.id,
        startTime: newStartTime,
        trackId: MAIN_TRACK_ID,
        originalStartTime: segmentFirstWord.start_time
      };
    }
  }
}

function getEndBoundaries({
  transcriptExtension,
  segment,
  segmentLastWord,
  segmentLastSelectionWord,
  transcriptSelection
}: {
  transcriptExtension?: TranscriptExtension | null;
  segment: CompositeSegment;
  segmentLastWord: Word;
  segmentLastSelectionWord: Word;
  transcriptSelection?: TranscriptSelection | null;
}) {
  const hasEndBoundary =
    transcriptExtension &&
    'endTime' in transcriptExtension &&
    'endSegmentId' in transcriptExtension &&
    transcriptExtension.endSegmentId === segment.id &&
    segmentLastWord?.end_time < (segmentLastSelectionWord?.end_time || segmentLastSelectionWord?.start_time);

  if (hasEndBoundary) {
    const boundariesWords = transcriptSelection?.words[segment.id]?.filter(word => {
      // set new end time for this segment
      return word.start_time < transcriptExtension.endTime;
    });

    const newEndTime = boundariesWords?.[boundariesWords.length - 1]?.end_time;
    if (newEndTime !== undefined) {
      return {
        segmentId: segment.id,
        endTime: newEndTime,
        trackId: MAIN_TRACK_ID,
        originalEndTime: segmentLastWord.end_time
      };
    }
  }
}
